import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./SocialBookSlot.css";

import Clock from "../../../../assets/icons/clock.gif";
const WHATSAPP_NUMBER = "919810044015";

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   PURE VALIDATOR FUNCTIONS
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */

export const validateEmail = (email) => {
  if (!email || !email.trim()) return "Email is required";
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) return "Invalid email format";
  const disposableDomains = [
    "mailinator.com", "10minutemail.com", "tempmail.com", "yopmail.com",
    "guerrillamail.com", "throwaway.email", "fakeinbox.com", "trashmail.com", "dispostable.com",
  ];
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return "Invalid email - missing domain";
  if (disposableDomains.includes(domain)) return "Disposable/temporary emails are not allowed";
  if (/^(test|abc|fake|temp|dummy|asdf|qwer|xxxx)/i.test(email)) return "Please use your real email address";
  return null;
};

export const validatePhone = (phone) => {
  const digits = (phone || "").trim();
  if (!digits) return "Phone number is required";
  if (!/^\d+$/.test(digits)) return "Phone number must contain only digits";
  if (digits.startsWith("0")) return "Phone number should not start with 0";
  if (digits.length !== 10) return "Phone number must be exactly 10 digits";
  if (!/^[6-9]\d{9}$/.test(digits)) return "Invalid Indian mobile number";
  return null;
};

export const validateWebsite = (url) => {
  if (!url || !url.trim()) return null;
  const trimmed = url.trim();
  const websiteRegex = /^(https?:\/\/)?([\w-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
  if (!websiteRegex.test(trimmed)) return "Enter a valid URL - e.g. abc.com or https://abc.com";
  const domain = trimmed.replace(/^https?:\/\//, "").split("/")[0];
  if (!domain.includes(".")) return "Website must have a valid domain - e.g. .com, .in, .org";
  return null;
};

export const validateFullName = (name) => {
  if (!name || !name.trim()) return "Full name is required";
  if (name.trim().length < 2) return "Name is too short";
  if (/\d/.test(name)) return "Name should not contain numbers";
  if (/[^a-zA-Z\s.'`\-]/.test(name)) return "Name contains invalid characters";
  return null;
};

export const validateArrivalTime = (time) => {
  if (!time) return "Arrival time is required";
  if (time < "12:30" || time > "14:30") return "Arrival time must be between 12:30 AM and 5:30 PM";
  return "";
};

export const validateCompany = (company) => {
  if (!company || !company.trim()) return "Company name is required";
  if (company.trim().length < 2) return "Company name is too short";
  return null;
};

/* â”€â”€ formatting helpers â”€â”€ */
const formatTimeToAMPM = (timeStr) => {
  if (!timeStr) return "N/A";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? "PM" : "AM"}`;
};

const getErrorMeta = (field) => ({
  fullName:          { placeholder: "Please enter your full name" },
  email:             { placeholder: "Enter a valid email address" },
  phone:             { placeholder: "Enter a valid phone number" },
  company:           { placeholder: "Enter your company name" },
  designation:       { placeholder: "Select or enter your designation" },
  reference:         { placeholder: "Please select a reference" },
  website:           { placeholder: "e.g. abc.com or https://abc.com" },
  arrivalTime:       { placeholder: "Choose time between 12:30 AM - 5:30 PM" },
  otherBusiness:     { placeholder: "Please specify your business" },
  otherDesignation:  { placeholder: "Please specify your designation" },
}[field] || {});

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   CUSTOM TIME PICKER  â€” compact professional redesign
   â€¢ Trigger shows selected time (read-only display)
   â€¢ Panel: three tight chip-grid columns â€” Hour | Min | AM/PM
   â€¢ No scroll wheels, no overflow â€” all options visible as chips
   â€¢ Valid range 12:30 AM â€“ 5:30 PM; invalid chips greyed + blocked
   â€¢ Stores 24-h "HH:MM" internally â€” all form logic unchanged
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */

function to12h(val24) {
  if (!val24) return { hour12: 11, minute: 0, period: "AM" };
  const [h, m] = val24.split(":").map(Number);
  return { hour12: h % 12 || 12, minute: m, period: h >= 12 ? "PM" : "AM" };
}
function to24h({ hour12, minute, period }) {
  let h = hour12 % 12;
  if (period === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}
function clamp(val24) {
  if (val24 < "12:30") return "12:30";
  if (val24 > "14:30") return "14:30";
  return val24;
}

/* valid hours per period inside 12:30â€“14:30 */
function isHourValid(h, period) {
  if (period === "AM") return h === 11;
  return [12, 1, 2, ].includes(h);
}
/* valid minutes for the currently selected hour+period */
function isMinuteValid(h, m, period) {
  const raw = to24h({ hour12: h, minute: m, period });
  return raw >= "12:30" && raw <= "14:30";
}

const HOURS   = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

function CustomTimePicker({ value, onChange, hasError }) {
  const { hour12, minute, period } = to12h(value);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  /* close on outside click */
  useEffect(() => {
    const fn = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const commit = useCallback((h12, min, per) => {
    onChange(clamp(to24h({ hour12: h12, minute: min, period: per })));
  }, [onChange]);

  const pickHour = (h) => {
    if (!isHourValid(h, period)) return;
    // if current minute becomes invalid, snap to 00
    const minOk = isMinuteValid(h, minute, period) ? minute : 0;
    commit(h, minOk, period);
  };

  const pickMinute = (m) => {
    if (!isMinuteValid(hour12, m, period)) return;
    commit(hour12, m, period);
  };

  const pickPeriod = (p) => {
    let h = hour12;
    if (!isHourValid(hour12, p)) h = p === "AM" ? 11 : 12;
    const raw = clamp(to24h({ hour12: h, minute, period: p }));
    onChange(raw);
  };

/* step ±15 min */
  const step = (delta) => {
    const [hh, mm] = value.split(":").map(Number);
    const d = new Date(); d.setHours(hh); d.setMinutes(mm + delta);
    onChange(clamp(d.toTimeString().slice(0, 5)));
  };

  const display = `${String(hour12).padStart(2, "0")} : ${String(minute).padStart(2, "0")}  ${period}`;

  return (
    <div ref={wrapRef} className={`ctp-wrap${hasError ? " ctp-wrap--err" : ""}`}>

      {/* â”€â”€ trigger â”€â”€ */}
      <button
        type="button"
        className={`ctp-trigger${open ? " ctp-trigger--open" : ""}`}
        onClick={() => setOpen(p => !p)}
      >
        <img src={Clock} alt="" className="ctp-clock-img" width="16" height="16" />
        <span className="ctp-display">{display}</span>
        <svg className="ctp-chevron" width="12" height="12" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s" }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {/* â”€â”€ expandable panel â”€â”€ */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="ctp-panel"
            initial={{ opacity: 0, scaleY: 0.92, originY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            exit   ={{ opacity: 0, scaleY: 0.92 }}
            transition={{ duration: 0.18, ease: [0.4,0,0.2,1] }}
          >
            {/* â”€â”€ three columns â”€â”€ */}
            <div className="ctp-body">

              {/* HOUR */}
              <div className="ctp-section">
                <div className="ctp-section-label">Hour</div>
                <div className="ctp-chips ctp-chips--4col">
                  {HOURS.map(h => {
                    const valid   = isHourValid(h, period);
                    const active  = h === hour12;
                    return (
                      <button key={h} type="button"
                        className={`ctp-chip${active ? " ctp-chip--on" : ""}${!valid ? " ctp-chip--dim" : ""}`}
                        onClick={() => pickHour(h)}
                        disabled={!valid}
                        title={!valid ? "Outside allowed range" : ""}
                      >
                        {String(h).padStart(2, "0")}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="ctp-divider" />

              {/* MINUTE */}
              <div className="ctp-section">
                <div className="ctp-section-label">Minute</div>
                <div className="ctp-chips ctp-chips--4col">
                  {MINUTES.map(m => {
                    const valid  = isMinuteValid(hour12, m, period);
                    const active = m === minute;
                    return (
                      <button key={m} type="button"
                        className={`ctp-chip${active ? " ctp-chip--on" : ""}${!valid ? " ctp-chip--dim" : ""}`}
                        onClick={() => pickMinute(m)}
                        disabled={!valid}
                      >
                        {String(m).padStart(2, "0")}
                      </button>
                    );
                  })}
                </div>
              </div>
    <div className="ctp-step-group">
                <button type="button" className="ctp-step-btn" onClick={() => step(15)}>
                  + 15 min
                </button>
                <button type="button" className="ctp-step-btn" onClick={() => step(-15)}>
                  {"\u2212"} 15 min
                </button>
              </div>
              <div className="ctp-divider" />

              {/* AM / PM */}
              <div className="ctp-section">
                <div className="ctp-section-label">Period</div>
                <div className="ctp-chips ctp-chips--2col">
                  {["AM", "PM"].map(p => (
                    <button key={p} type="button"
                      className={`ctp-chip ctp-chip--period${p === period ? " ctp-chip--on" : ""}`}
                      onClick={() => pickPeriod(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                  <button type="button" className="ctp-done-btn" onClick={() => setOpen(false)}>
                Done {"\u2713"}
              </button>
              </div>
            </div>



          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   MAIN COMPONENT
â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
export default function SocialBookSlot({ isOpen, onClose, eventData, isRegistrationOpen = true }) {
const [copied, setCopied] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors,    setErrors]    = useState({});
  const [touched,   setTouched]   = useState({});

  const [formData, setFormData] = useState({
    fullName: "", email: "", phone: "", company: "",
    designation: "", reference: "", website: "", arrivalTime: "12:30",
    coreBusiness: [], otherBusiness: "", otherDesignation: ""
  });

  /* â”€â”€ reset on open â”€â”€ */
  useEffect(() => {
    if (isOpen) {
      setSubmitted(false); setErrors({}); setTouched({});
      setFormData({
        fullName: "", email: "", phone: "", company: "",
        designation: "", reference: "", website: "", arrivalTime: "12:30",
        coreBusiness: [], otherBusiness: "", otherDesignation: ""
      });
    }
  }, [isOpen]);
useEffect(() => {
  if (isOpen && (eventData?.city || eventData?.location)) {
    const citySlug = (eventData.city || eventData.location || '')
      .toLowerCase()
      .replace(/\s+/g, '-');

    const rsvpUrl = `/events/social/rsvp/${citySlug}`;
    window.history.pushState(
      { rsvp: true, city: citySlug },
      '',
      rsvpUrl
    );
  }
  if (!isOpen) {
    const currentPath = window.location.pathname;
    if (currentPath.includes('/events/social/rsvp/')) {
      window.history.pushState({}, '', '/events/social');
    }
  }
}, [isOpen, eventData?.city, eventData?.location]); // ← use primitives, not the whole object


useEffect(() => {
  const handlePopState = (e) => {
    if (e.state?.rsvp) return; // navigating to rsvp â€” let it be
    // navigating away from rsvp â€” close modal
    if (onClose) onClose();
  };

  window.addEventListener('popstate', handlePopState);
  return () => window.removeEventListener('popstate', handlePopState);
}, [onClose]);
  /* â”€â”€ per-field validator â”€â”€ */
  const validateField = (name, value) => {
    switch (name) {
      case "fullName":          return validateFullName(value);
      case "email":             return validateEmail(value);
      case "phone":             return validatePhone(value);
      case "company":           return validateCompany(value);
      case "website":           return validateWebsite(value);
      case "arrivalTime":       return validateArrivalTime(value);
      case "designation":       return (!value || !value.trim()) ? "Designation is required" : null;
      case "otherDesignation":  return (!value || !value.trim()) ? "Please specify your designation" : null;
      case "reference":         return (!value || !value.trim()) ? "Please select who referred you" : null;
      case "otherBusiness":     return (!value || !value.trim()) ? "Please specify your business area" : null;
      default:                  return null;
    }
  };

  /* â”€â”€ onChange â”€â”€ */
  const handleChange = (e) => {
    const { name, value } = e.target;
    let clean = value;

    if (name === "phone") {
      clean = value.replace(/[^0-9]/g, "");
      if (clean.length > 10) clean = clean.slice(0, 10);
      if (clean.startsWith("0")) {
        setErrors(p => ({ ...p, phone: "Phone number should not start with 0" }));
      } else if (touched.phone) {
        const err = validatePhone(clean);
        setErrors(p => ({ ...p, phone: err || undefined }));
      } else {
        setErrors(p => ({ ...p, phone: undefined }));
      }
    } else {
      setErrors(p => ({ ...p, [name]: undefined }));
    }

    setFormData(p => ({ ...p, [name]: clean }));
  };

  /* â”€â”€ onBlur â”€â”€ */
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(p => ({ ...p, [name]: true }));
    const err = validateField(name, value);
    setErrors(p => ({ ...p, [name]: err || undefined }));
  };

  /* â”€â”€ arrival time change from CustomTimePicker â”€â”€ */
  const handleTimeChange = (val24) => {
    setFormData(p => ({ ...p, arrivalTime: val24 }));
    setTouched(p => ({ ...p, arrivalTime: true }));
    const err = validateArrivalTime(val24);
    setErrors(p => ({ ...p, arrivalTime: err || undefined }));
  };

  const toggleCoreBusiness = (item) => {
    if (errors.coreBusiness) setErrors(p => ({ ...p, coreBusiness: undefined }));
    setFormData(p => ({
      ...p,
      coreBusiness: p.coreBusiness.includes(item)
        ? p.coreBusiness.filter(i => i !== item)
        : [...p.coreBusiness, item]
    }));
    setTouched(p => ({ ...p, coreBusiness: true }));
  };

  /* â”€â”€ full validation on submit â”€â”€ */
  const validateAllFields = () => {
    const e = {};
    const nameErr    = validateFullName(formData.fullName);   if (nameErr)    e.fullName    = nameErr;
    const emailErr   = validateEmail(formData.email);          if (emailErr)   e.email       = emailErr;
    const phoneErr   = validatePhone(formData.phone);          if (phoneErr)   e.phone       = phoneErr;
    const companyErr = validateCompany(formData.company);      if (companyErr) e.company     = companyErr;
    const websiteErr = validateWebsite(formData.website);      if (websiteErr) e.website     = websiteErr;
    const timeErr    = validateArrivalTime(formData.arrivalTime); if (timeErr) e.arrivalTime = timeErr;

    if (!formData.designation.trim()) e.designation = "Designation is required";
    if (formData.designation === "Other" && !formData.otherDesignation.trim())
      e.otherDesignation = "Please specify your designation";
    if (!formData.reference) e.reference = "Please select who referred you";
    if (formData.coreBusiness.length === 0)
      e.coreBusiness = "Please select at least one core business area";
    if (formData.coreBusiness.includes("Other") && !formData.otherBusiness.trim())
      e.otherBusiness = "Please specify your other business area";

    setErrors(e);
    setTouched({
      fullName: true, email: true, phone: true, company: true, designation: true,
      reference: true, website: true, arrivalTime: true, coreBusiness: true,
      otherBusiness: true, otherDesignation: true
    });
    return Object.keys(e).length === 0;
  };

  /* â”€â”€ submit â”€â”€ */
  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setLoading(true);

    const isValid = validateAllFields();
    if (!isValid) {
      setLoading(false);
      setTimeout(() => {
        const firstErr = document.querySelector(".error-input");
        if (firstErr) firstErr.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }

    const SCRIPT_URL = eventData?.scriptUrl || "";
    if (!SCRIPT_URL) { alert("Configuration Error: Script URL is missing."); setLoading(false); return; }

    const formattedTime = formatTimeToAMPM(formData.arrivalTime);
    let businessString = formData.coreBusiness.filter(b => b !== "Other").join(", ");
    if (formData.coreBusiness.includes("Other") && formData.otherBusiness)
      businessString += (businessString ? `, Other: ${formData.otherBusiness}` : `Other: ${formData.otherBusiness}`);

    const finalDesignation = formData.designation === "Other" ? formData.otherDesignation : formData.designation;
    const fullPhone = formData.phone;

    const message =
      `Hello Team IHN,

I would love to attend:
📌 Event: *${eventData?.title || "N/A"}*
📍 Venue: ${eventData?.city || eventData?.location || "N/A"}
📅 Date: ${eventData?.date ? new Date(eventData.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : "N/A"}
⏳ Expected Time of Arrival: ${formattedTime}

*My Credentials:*
👤 Name: ${formData.fullName}
🏢 Company: ${formData.company}
💼 Designation: ${finalDesignation}
📞 Phone: ${fullPhone}
📧 Email: ${formData.email}
🌐 Website: ${formData.website || "N/A"}
🧩 Core Business: ${businessString || "N/A"}

Invitation Reference: ${formData.reference}

Kindly confirm my RSVP.
Thank you!`;

    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, "_blank");

    fetch(SCRIPT_URL, {
     method: "POST",
  mode: "no-cors",
  headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({
        "Email Address":               formData.email,
        "YOUR NAME":                   formData.fullName,
        "DESIGNATION":                 finalDesignation,
        "NAME OF YOUR COMPANY":        formData.company,
        "MOBILE NUMBER":               fullPhone,
        "Your Website URL":            formData.website || "",
        "CORE BUSINESS AREA":          businessString,
        "EXPECTED TIME OF ARRIVAL !":  formattedTime,
        "Invitation Reference":        formData.reference,
       "City": eventData?.city || eventData?.location || ""
      })
    }).then(() => setSubmitted(true))
      .catch(() => alert("Saved partially. Please contact support."))
      .finally(() => setLoading(false));
  };

  /* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
     INLINE STYLES (unchanged from original)
  â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
  const S = {
    overlay: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
      backdropFilter: "blur(6px)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 9999
    },
    modal: {
      background: "#fff", width: "95%", maxWidth: 460, borderRadius: 18,
      padding: "28px 26px 30px", position: "relative",
      boxShadow: "0 25px 60px rgba(0,0,0,0.25)", overflowY: "auto", maxHeight: "90vh"
    },
    closeBtn: {
      position: "absolute", top: 14, right: 16, background: "transparent",
      border: "none", width: 40, height: 40, minWidth: 40, minHeight: 40,
      padding: 0, borderRadius: "50%", display: "flex", alignItems: "center",
      justifyContent: "center", fontSize: 28, cursor: "pointer", color: "#555",
      lineHeight: 1, flexShrink: 0, zIndex: 2
    },
    form: { display: "flex", flexDirection: "column", gap: 14 },
    input: (err) => ({
      width: "100%", padding: "12px 14px", borderRadius: 10, fontSize: 14,
      border: "1px solid " + (err ? "red" : "#ddd"),
      background: "#fafafa", color: "#333", boxSizing: "border-box",
      transition: "border-color 0.2s, box-shadow 0.2s, background 0.2s",
      boxShadow: err ? "0 0 0 1px rgba(255, 0, 0, 0.08)" : "none",
      outline: "none"
    }),
    select: (err) => ({
      width: "100%", padding: "12px 14px", borderRadius: 10, fontSize: 14,
      border: err ? "2px solid red" : "1px solid #ddd",
      background: "#fafafa", color: "#333", boxSizing: "border-box",
      cursor: "default", outline: "none"
    }),
    cbGrid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10, marginTop: 6 },
    cbLabel: (checked) => ({
      display: "flex", alignItems: "center", gap: 10, padding: "11px 13px",
      borderRadius: 12, cursor: "pointer", fontSize: 13,
      background: checked ? "#f2e9f0" : "#f8f8f8",
      border: checked ? "1px solid #861b68" : "1px solid #ddd",
      transition: "all 0.2s", userSelect: "none", color: "#444"
    }),
    cbBox: (checked) => ({
      width: 18, height: 18, borderRadius: 4, border: "2px solid #861b68",
      background: checked ? "#861b68" : "#fff", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 11, color: "#fff", transition: "background 0.2s"
    }),
    errText: { color: "red", fontSize: 12, marginTop: 3 },
    label:   { fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 2 },
    tyContainer: { textAlign: "center" },
    tyIcon: {
      width: 70, height: 70, margin: "0 auto 14px", borderRadius: "50%",
      background: "rgba(134,27,104,0.12)", display: "flex",
      alignItems: "center", justifyContent: "center", fontSize: 36,
      fontWeight: 700, color: "#861b68"
    },
    waCta: {
      display: "inline-flex", alignItems: "center", gap: 10,
      padding: "12px 18px", background: "#861b68", color: "#fff",
      borderRadius: 50, fontSize: 14, fontWeight: 600, textDecoration: "none"
    }
  };

  /* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
     RENDER
  â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div style={S.overlay}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div style={S.modal}
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1,   y: 0,  opacity: 1 }}
            exit   ={{ scale: 0.9, y: 20, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              style={S.closeBtn}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={onClose}
              aria-label="Close booking modal"
            >
              ×
            </button>
            {/* â”€â”€ THANK YOU â”€â”€ */}
            {submitted ? (
              <div style={S.tyContainer}>
                <div style={S.tyIcon}>✓</div>
                <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Request Received!</h3>
                <p style={{ fontSize: 14, color: "#555", marginBottom: 22 }}>
                  Thank you for showing interest in this event.<br />
                  We've successfully received your participation request.<br />
                  Our team will review your registration and contact you within <strong>two working days</strong>.
                </p>
                <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" style={S.waCta}>
                  💬 Chat with us on WhatsApp
                </a>
              </div>

            ) : !isRegistrationOpen ? (
              <div style={S.tyContainer}>
                <div style={S.tyIcon}>⏳</div>
                <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Registration Not Started</h3>
                <p style={{ fontSize: 14, color: "#555", marginBottom: 22 }}>
                  Registrations for this event are not open yet.<br />Please check back soon.
                </p>
                <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" style={S.waCta}>
                  💬 Chat with us on WhatsApp
                </a>
              </div>

            ) : (
              /* â”€â”€ FORM â”€â”€ */
              <form style={S.form} onSubmit={handleSubmit}>
                <h3 className="heading">Book Your Slot</h3>
             {/* Email */}
                <div>
                  <input
                    name="email" type="email"
                    placeholder={errors.email ? getErrorMeta("email").placeholder : "Email *"}
                    value={formData.email}
                    onChange={handleChange} onBlur={handleBlur}
                    style={S.input(errors.email)}
                    className={errors.email ? "error-input shake" : ""}
                  />
                  {errors.email && <p style={S.errText}>⚠ {errors.email}</p>}
                </div>

                {/* Full Name */}
                <div>
                  <input
                    name="fullName" type="text"
                    placeholder={errors.fullName ? getErrorMeta("fullName").placeholder : "Full Name *"}
                    value={formData.fullName}
                    onChange={handleChange} onBlur={handleBlur}
                    style={S.input(errors.fullName)}
                    className={errors.fullName ? "error-input shake" : ""}
                  />
                  {errors.fullName && <p style={S.errText}>⚠ {errors.fullName}</p>}
                </div>

                {/* Designation */}
                <div>
                  <select
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange} onBlur={handleBlur}
                    style={S.select(errors.designation)}
                    className={errors.designation ? "error-input" : ""}
                  >
                    <option value="">Select Designation *</option>
                    {["Founder","Co-Founder","Managing Director","Director",
                      "Chief Executive Officer (CEO)","Chief Operating Officer (COO)",
                      "Manager","Assistant Vice President","Head of Contracting",
                      "Partner","Proprietor","Senior Manager","Project Manager",
                      "Operations Manager","Other"].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  {errors.designation && <p style={S.errText}>⚠ {errors.designation}</p>}
                </div>

                {formData.designation === "Other" && (
                  <div>
                    <input
                      name="otherDesignation"
                      placeholder="Please specify your designation *"
                      value={formData.otherDesignation}
                      onChange={handleChange} onBlur={handleBlur}
                      style={S.input(errors.otherDesignation)}
                      className={errors.otherDesignation ? "error-input shake" : ""}
                    />
                    {errors.otherDesignation && <p style={S.errText}>⚠ {errors.otherDesignation}</p>}
                  </div>
                )}

                {/* Company */}
                <div>
                  <input
                    name="company" type="text"
                    placeholder={errors.company ? getErrorMeta("company").placeholder : "Company Name *"}
                    value={formData.company}
                    onChange={handleChange} onBlur={handleBlur}
                    style={S.input(errors.company)}
                    className={errors.company ? "error-input shake" : ""}
                  />
                  {errors.company && <p style={S.errText}>⚠ {errors.company}</p>}
                </div>

                {/* Phone */}
                <div>
                  <input
                    name="phone" type="tel" inputMode="numeric" maxLength={10}
                    placeholder="Phone Number *"
                    value={formData.phone}
                    onChange={handleChange} onBlur={handleBlur}
                    style={S.input(errors.phone)}
                    className={errors.phone ? "error-input shake" : ""}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    <span style={{ fontSize: 11, color: errors.phone ? "red" : "#999" }}>
                      Enter 10-digit mobile number
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 600,
                      color: formData.phone.length === 10 && !errors.phone ? "#27ae60"
                           : errors.phone ? "red" : "#f39c12",
                    }}>
                      {formData.phone.length}/10
                    </span>
                  </div>
                  {errors.phone && <p style={S.errText}>⚠ {errors.phone}</p>}
                </div>

                {/* Website */}
                <div>
                  <input
                    name="website"
                    placeholder="Website URL (optional)"
                    value={formData.website}
                    onChange={handleChange} onBlur={handleBlur}
                    style={S.input(errors.website)}
                    className={errors.website ? "error-input shake" : ""}
                  />
                  {errors.website && <p style={S.errText}>⚠ {errors.website}</p>}
                </div>

                {/* Core Business */}
                <div>
                  <span style={S.label}>Core Business Area *</span>
                  <div style={{
                    ...S.cbGrid,
                    borderRadius: 10,
                    padding: errors.coreBusiness ? "8px" : "0",
                    border: errors.coreBusiness ? "1.5px solid red" : "none",
                    marginTop: 8
                  }}>
                    {["Outbound","Inbound","Corporate","Luxury / HNI","MICE","Events",
                      "Groups","Hotels","Airline","Other"].map(item => {
                      const checked = formData.coreBusiness.includes(item);
                      return (
                        <label key={item} style={S.cbLabel(checked)} onClick={() => toggleCoreBusiness(item)}>
                           <span style={S.cbBox(checked)}>{checked ? "✓" : ""}</span>
                          {item}
                        </label>
                      );
                    })}
                  </div>
                  {errors.coreBusiness && <p style={S.errText}>⚠ {errors.coreBusiness}</p>}
                </div>

                {formData.coreBusiness.includes("Other") && (
                  <div className="other-business-container">
                    <input
                      name="otherBusiness"
                      placeholder="Please specify other business"
                      value={formData.otherBusiness}
                      onChange={handleChange}
                      className={errors.otherBusiness ? "error-input" : ""}
                    />
                  </div>
                )}

                {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
                    ARRIVAL TIME + REFERRED BY ROW
                    Time input is replaced with CustomTimePicker
                â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
                <div className="ref-time-row">

                  {/* Arrival Time — CustomTimePicker */}
                  <div className="bookslot-field col-sm-6">
                    <label className="time-label">Arrival Time</label>
                    <CustomTimePicker
                      value={formData.arrivalTime}
                      onChange={handleTimeChange}
                      hasError={!!errors.arrivalTime}
                    />
                    {errors.arrivalTime ? (
                      <small className="ctp-hint ctp-hint--error">⚠ {errors.arrivalTime}</small>
                    ) : (
                      <small className="ctp-hint">Select 12:30 AM - 2:30 PM only</small>
                    )}
                  </div>

                  {/* Referred By */}
                  <div className="bookslot-field col-sm-6">
                    <label className="time-label">Referred By</label>
                    <select
                      name="reference"
                      value={formData.reference}
                      onChange={handleChange}
                      style={{ borderRadius: "8px", fontSize: "14px", paddingLeft: "5px", paddingRight: "5px" }}
                      className={errors.reference ? "error-input" : ""}
                    >
                      <option value="">Invite by Reference</option>
                      <option value="Punam Singh">Punam Singh</option>
                      <option value="Abhishek Garg">Abhishek Garg</option>
                    </select>
                    <small className="time-placeholder">Select any one of reference</small>
                  </div>

                </div>

                <motion.button
                  type="submit"
                  className="submit-booking-btn"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Send Request"}
                </motion.button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}



